import React from 'react';
import { createPortal } from 'react-dom';

import {
  ADD_RECORD_CLOSE_LABEL,
  ADD_VARIETY_LABELS,
} from 'constants/uiConstants';

import AddVarietyForm from './AddVarietyForm';
import FlyoutHeader from './FlyoutHeader';
import { StepSelectorOption } from './stepConfig';
import useFlyoutPosition from './useFlyoutPosition';

interface AddVarietyFlyoutProps {
  anchorRef: React.RefObject<HTMLElement>;
  /** The crop the variety is linked to. */
  crop: StepSelectorOption;
  initialName: string;
  onClose: () => void;
  /** Called with the crop and new variety as one selection. */
  onCreated: (selection: StepSelectorOption) => void;
}

/**
 * The Add variety form portaled beside the step picker (OP-2498). Closing
 * without saving sends nothing.
 */
const AddVarietyFlyout: React.FC<AddVarietyFlyoutProps> = ({
  anchorRef,
  crop,
  initialName,
  onClose,
  onCreated,
}) => {
  const { position, portalContainer } = useFlyoutPosition(anchorRef);

  if (!position || !portalContainer) return null;

  return createPortal(
    <div
      style={
        {
          '--flyout-top': `${position.top}rem`,
          '--flyout-left': `${position.left}rem`,
          '--flyout-width': `${position.width}rem`,
          '--flyout-height': `${position.height}rem`,
        } as React.CSSProperties
      }
      tabIndex={-1}
      className="step-picker-flyout z-[1090] flex flex-col overflow-hidden rounded-l-none rounded-lg border-l bg-white outline-none"
      onKeyDown={(event) => {
        // Escape closes this form only, not the picker behind it.
        if (event.key === 'Escape') {
          event.stopPropagation();
          event.nativeEvent.stopImmediatePropagation();
          onClose();
        }
      }}
    >
      <FlyoutHeader
        title={`${ADD_VARIETY_LABELS.title}: ${crop.label}`}
        closeLabel={ADD_RECORD_CLOSE_LABEL}
        onClose={onClose}
      />
      <AddVarietyForm
        crop={crop}
        initialName={initialName}
        onCreated={onCreated}
      />
    </div>,
    portalContainer
  );
};

export default AddVarietyFlyout;
